<template>
  <div class="fsd-page">
    <FansStoreChrome @home="goHome" />

    <main class="fsd-container">
      <div class="fsd-crumb">
        当前位置：
        <a href="javascript:void(0)" @click.prevent="goHome">首页</a>
        <span> > </span>
        <a href="javascript:void(0)" @click.prevent="goCategory">{{ product.category || 'Telegram服务' }}</a>
        <span> > </span>
        <span>{{ product.title }}</span>
      </div>

      <section class="fsd-top">
        <div class="fsd-icon-row">
          <div class="fsd-icon" :class="`fsd-icon--${iconClassFor(product.icon)}`" aria-hidden="true">
            {{ product.icon || '✈️' }}
          </div>
          <div class="fsd-title">{{ product.title }}</div>
        </div>

        <div class="fsd-form-row">
          <input
            v-model="account"
            class="fsd-input"
            type="text"
            :placeholder="product.placeholder || '请输入账号链接'"
          />
          <input
            v-model="qty"
            class="fsd-input"
            type="number"
            min="1"
            :placeholder="qtyPlaceholder"
            @input="onQtyInput"
          />
        </div>

        <div class="fsd-meta">
          <span class="fsd-price">
            价格：
            <span class="fsd-price-value">$ {{ displayTotal }}</span>
            <span v-if="product.priceText" class="fsd-price-unit">（{{ product.priceText }}）</span>
          </span>
          <button type="button" class="fsd-btn" :disabled="loading" @click="onPay">
            {{ loading ? '提交中...' : '立即支付' }}
          </button>
        </div>
      </section>

      <section class="fsd-info">
        <div class="fsd-warning">说明：</div>
        <hr />
        <div class="fsd-desc">
          <template v-if="product.descLines && product.descLines.length">
            <p v-for="(line, idx) in product.descLines" :key="idx">{{ line }}</p>
          </template>
        </div>
      </section>
    </main>

    <footer class="fsd-footer">Fans Store - 粉丝商城 © 2025</footer>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import FansStoreChrome from '../components/FansStoreChrome.vue';
import { addOrder } from '../util/api';
import {
  calcFansTotal,
  fetchFansServices,
  iconClassFor,
  normalizeFansService,
} from '../data/fansStore';
import { buildReferralQuery, resolveReferralContext } from '../util/deeplink';
import { makeOrderNo, pushSelectWallet } from '../util/paymentFlow';

export default {
  name: 'FansStoreDetail',
  components: { FansStoreChrome },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const account = ref('');
    const qty = ref('');
    const loading = ref(false);
    const product = ref({
      title: String(route.query.title || ''),
      category: 'Telegram服务',
      placeholder: '请输入账号链接',
      descLines: [],
      icon: '✈️',
      priceText: '',
      unitPrice: 0,
      unit: 1,
    });
    const referralCode = ref('');
    const gameGroupId = ref('');

    const qtyNumber = computed(() => Number(qty.value) || 0);
    const total = computed(() => calcFansTotal(product.value, qtyNumber.value));
    const displayTotal = computed(() => total.value.toFixed(2));
    const qtyPlaceholder = computed(() => {
      const unit = Number(product.value.unit) || 0;
      return unit > 1 ? `请输入数量（计价单位 ${unit}）` : '请输入数量';
    });

    const homeQuery = () => buildReferralQuery(referralCode.value, gameGroupId.value);

    async function loadProduct() {
      const ctx = resolveReferralContext(route);
      referralCode.value = ctx.referralCode;
      gameGroupId.value = ctx.gameGroupId;

      const title = decodeURIComponent(String(route.query.title || ''));
      const id = route.query.id != null && route.query.id !== '' ? String(route.query.id) : '';
      if (title) product.value = { ...product.value, title };

      try {
        const list = (await fetchFansServices()) || [];
        const hit = id
          ? list.find((item) => String(item.id) === id)
          : list.find((item) => item.title === title);
        if (hit) {
          product.value = normalizeFansService(hit);
          // 数量为空时默认填入计价单位，价格立刻可见（与列表单价一致）
          if (!qty.value && product.value.unit > 0) {
            qty.value = String(product.value.unit);
          }
        }
      } catch (e) {
        console.error(e);
      }
    }

    onMounted(loadProduct);
    watch(() => [route.query.id, route.query.title], loadProduct);

    const onQtyInput = () => {
      // 触发计算依赖 qty；保留函数便于后续校验
    };

    const goHome = () => {
      router.push({ path: '/fansStore', query: homeQuery() });
    };

    const goCategory = () => {
      router.push({
        path: '/fansStore',
        query: {
          ...homeQuery(),
          category: product.value.category,
        },
      });
    };

    const onPay = async () => {
      if (!account.value.trim()) {
        message.warning(product.value.placeholder || '请输入账号链接');
        return;
      }
      if (!qtyNumber.value || qtyNumber.value <= 0) {
        message.warning('请输入数量');
        return;
      }
      if (!product.value.unitPrice) {
        message.warning('商品价格无效，请返回重试');
        return;
      }

      const data = {
        title: product.value.title || '粉丝商城',
        price: total.value,
        inputNumber: 1,
        inputGT: `${account.value.trim()} / 数量${qtyNumber.value}`,
        orderNo: makeOrderNo(),
        referral_code: referralCode.value,
      };

      loading.value = true;
      try {
        await addOrder(data);
        pushSelectWallet(router, data, gameGroupId.value);
      } catch (e) {
        console.error(e);
        message.error('下单失败，请稍后重试');
      } finally {
        loading.value = false;
      }
    };

    return {
      product,
      account,
      qty,
      loading,
      displayTotal,
      qtyPlaceholder,
      iconClassFor,
      onQtyInput,
      goHome,
      goCategory,
      onPay,
    };
  },
};
</script>

<style scoped>
.fsd-page {
  min-height: 100vh;
  min-height: 100dvh;
  background: #f5f5f5;
  color: #333;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
  padding-bottom: 90px;
  box-sizing: border-box;
}

.fsd-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px 40px;
  box-sizing: border-box;
}

.fsd-crumb {
  font-size: 14px;
  color: #666;
  margin: 20px 0 30px;
  padding-left: 12px;
  line-height: 1.6;
}

.fsd-crumb a {
  color: #4a76df;
  text-decoration: none;
}

.fsd-top {
  background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.fsd-icon-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.fsd-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 34px;
  line-height: 1;
}

.fsd-icon--orange {
  background: linear-gradient(135deg, #ff9a56, #ff6b35);
}

.fsd-icon--teal {
  background: linear-gradient(135deg, #4ecdc4, #44a08d);
}

.fsd-icon--blue {
  background: linear-gradient(135deg, #667eea, #764ba2);
}

.fsd-title {
  font-size: 20px;
  font-weight: 600;
  color: #1a2b49;
}

.fsd-form-row {
  display: flex;
  gap: 12px;
}

.fsd-input {
  flex: 1;
  min-width: 0;
  font-size: 15px;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fff;
  min-height: 40px;
  box-sizing: border-box;
  outline: none;
}

.fsd-input:focus {
  border-color: #4a76df;
  box-shadow: 0 0 8px rgba(74, 118, 223, 0.2);
}

.fsd-meta {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.fsd-price {
  color: #d32f2f;
  font-size: 16px;
  font-weight: 500;
}

.fsd-price-value {
  font-size: 20px;
  font-weight: 600;
  margin-left: 4px;
}

.fsd-price-unit {
  margin-left: 6px;
  font-size: 13px;
  font-weight: 400;
  color: #999;
}

.fsd-btn {
  background: linear-gradient(90deg, #4a76df, #5e92f3);
  color: #fff;
  border: 0;
  padding: 10px 28px;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
}

.fsd-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.fsd-info {
  background: #f9fbfd;
  border: 1px solid #e0e7ff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.fsd-warning {
  color: #d32f2f;
  font-size: 15px;
  font-weight: 500;
  margin-bottom: 16px;
}

.fsd-info hr {
  border: none;
  border-top: 1px solid #e0e7ff;
  margin: 16px 0;
}

.fsd-desc {
  font-size: 15px;
  color: #444;
  line-height: 1.6;
}

.fsd-desc p {
  margin: 0 0 4px;
}

.fsd-footer {
  width: 100%;
  background: linear-gradient(90deg, #16233a 0%, #22395c 100%);
  color: #fff;
  text-align: center;
  padding: 22px 0 18px;
  font-size: 15px;
  letter-spacing: 1px;
  position: fixed;
  left: 0;
  bottom: 0;
  z-index: 100;
}

@media (max-width: 700px) {
  .fsd-container { padding: 0 10px 40px; }
  .fsd-top { padding: 16px; }
  .fsd-title { font-size: 18px; }
  .fsd-form-row { flex-direction: column; }
  .fsd-meta { flex-direction: column; align-items: stretch; }
  .fsd-btn { width: 100%; padding: 12px 0; }
  .fsd-footer { font-size: 12px; padding: 15px 0 12px; }
}
</style>
